import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircleQuestionMark } from 'lucide-react';
import { IconButton, ICON_SIZE } from '../design-system';
import { useSelector } from '@legendapp/state/react';
import CommentControlBar from './CommentControlBar';
import Sidebar from './Sidebar';
import GlobalAIChatPanel, { GlobalAIChatToggle } from './GlobalAIChatPanel';
import ExtensionModal from './ExtensionModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import EditTitleModal from './EditTitleModal';
import TagEditModal from './TagEditModal';
import GlobalUploadModal from './GlobalUploadModal';
import { RAGChatProvider } from '../context/RAGChatContext';
import { HeaderSlotProvider, useHeaderSlotRegistry } from '../context/HeaderSlotContext';
import { ui$ } from '../store';
import { openFeedbackWidget } from '../lib/featurebase';
import { openRAGPanel, closeRAGPanel } from '../store/ui$';
import { useMobileShell } from '../hooks/useMobileShell';
import MobileTopBar from './mobile/MobileTopBar';
import MobileTabBar from './mobile/MobileTabBar';
import { useContentWidthGuard } from '../hooks/useContentWidthGuard';
import TrialBanner from './TrialBanner';
import NoticeBanner from './NoticeBanner';
import TrialEndSummary from './TrialEndSummary';
import GuidedOnboardingProvider from './onboarding/v2/GuidedOnboarding';

const Layout = ({ children, auth }) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const { listId } = useParams();

  // The shell owns the content-width floor: it reclaims the sidebar first, then the panel.
  useContentWidthGuard();

  // True list views: pages that show an article list (All, Inbox, named lists)
  // Note: /deck/ routes are excluded - RAG chat only works on article lists, not flashcard decks
  const isListView = location.pathname === '/all' ||
                     location.pathname === '/inbox' ||
                     location.pathname.startsWith('/list/');

  // Content pages: a title and the actions that belong to that page, and no
  // list of their own. Their title section is portalled into the shell's bar so
  // the content surface starts at the content.
  const isContentPage = ['/dashboard', '/me', '/decks'].includes(location.pathname)
    || location.pathname.startsWith('/settings') || location.pathname === '/me/usage';

  // Routes where the global lib AI chat panel should be available.
  // Dashboard is not a list view but still gets lib chat for consistency.
  const isLibChatEnabled = isListView || location.pathname === '/dashboard';

  // Determine scope for RAG chat
  const getScopeType = () => {
    if (location.pathname === '/all') return 'all';
    if (location.pathname === '/inbox') return 'inbox';
    if (location.pathname.startsWith('/list/')) return 'list';
    return 'all';
  };

  const scopeType = getScopeType();
  const scopeId = scopeType === 'list' ? listId : null;
  const ragPanelHidden = useSelector(() => ui$.ragPanelHidden?.get() ?? true);
  const isAIPanelActuallyVisible = useSelector(() => ui$.isAIPanelActuallyVisible?.get() ?? false);
  const sidebarOpen = useSelector(() => ui$.sidebarOpen?.get() ?? false);
  const sidebarCollapsed = useSelector(() => ui$.sidebarCollapsed?.get() ?? false);
  const showExtensionModal = useSelector(() => ui$.activeModal.get() === 'extension');
  const showConfirmDeleteModal = useSelector(() => ui$.activeModal.get() === 'confirmDelete');
  const showEditTitleModal = useSelector(() => ui$.activeModal.get() === 'editTitle');
  const showTagEditModal = useSelector(() => ui$.activeModal.get() === 'editTags');
  const isArticleView = location.pathname.startsWith('/article/');
  const isDeckView = location.pathname.startsWith('/deck/');
  // Search is a phone-shell destination with its own bar, like the reader and
  // the deck; desktop never routes here (its search is the Cmd+K modal).
  const isSearchView = location.pathname === '/search';
  // The review side of the app does not offer article upload: Decks' own title
  // section already ends in a +, and it means "new deck".
  const isReviewRoute = isDeckView || location.pathname === '/decks';

  // Phone shell: bottom tabs own navigation, so the sidebar and the app header
  // both stand down. Reader and deck are the exceptions that prove the rule —
  // each is a destination you push into, each supplies its own bar with Back in
  // it, so a tab bar underneath would be a second exit for the same screen.
  const isMobileShell = useMobileShell();
  const isPushedDestination = isArticleView || isDeckView || isSearchView;

  // The phone shell reaches `body` for one reason: third-party chrome mounts
  // there, outside `.app-container`, so a container class cannot reach it.
  useEffect(() => {
    document.body.classList.toggle('mobile-shell', isMobileShell);
    return () => document.body.classList.remove('mobile-shell');
  }, [isMobileShell]);

  const showsMobileTopBar = isMobileShell && isListView;
  const showsMobilePageBar = isMobileShell && isContentPage;
  const showsMobileTabBar = isMobileShell && !isPushedDestination;
  const showsReaderChrome = isMobileShell && isArticleView;
  // NOTE: Removed custom realtime subscription for article updates.
  // syncedSupabase's built-in realtime: true config handles this automatically.
  // The custom subscription was causing a loop: assign() → sync → DB update → realtime → assign()

  // Wrap lib-chat-enabled views with RAG chat provider and panel
  const headerSlots = useHeaderSlotRegistry();

  const renderContent = () => {
    if (isLibChatEnabled && auth.isAuthenticated) {
      return (
        <RAGChatProvider
          scopeType={scopeType}
          scopeId={scopeId}
          userId={auth.user?.id}
        >
          <div className="app-main">
            {/* Remote notices (ADR-007, public — shows even signed out).
                P4-fix: lives at the top of the scroll container, sticky, so the
                fixed header can never cover it (2.0.3 painted it underneath). */}
            <NoticeBanner />
            {children}
          </div>
          {/* RAG Chat Panel - shown on list views and dashboard */}
          <GlobalAIChatPanel onClose={closeRAGPanel} />
          <GlobalAIChatToggle onOpen={openRAGPanel} />
        </RAGChatProvider>
      );
    }
    return (
      <div className="app-main">
        {/* Remote notices (ADR-007, public — shows even signed out).
            P4-fix: lives at the top of the scroll container, sticky, so the
            fixed header can never cover it (2.0.3 painted it underneath). */}
        <NoticeBanner />
        {children}
      </div>
    );
  };

  // Build container classes for AI panel layout adjustment
  const containerClasses = [
    'app-container',
    'with-sidebar',
    isArticleView ? 'is-article-view' : '',
    isListView ? 'is-list-view' : '',
    isDeckView ? 'is-deck-view' : '',
    isMobileShell ? 'mobile-shell' : '',
    showsMobileTopBar || showsMobilePageBar ? 'has-mobile-top-bar' : '',
    showsMobileTabBar ? 'has-mobile-tab-bar' : '',
    showsReaderChrome ? 'has-mobile-reader-chrome' : '',
    sidebarOpen ? 'mobile-sidebar-open' : '',
    sidebarCollapsed ? 'sidebar-collapsed' : '',
    isLibChatEnabled && !ragPanelHidden ? 'with-rag-panel' : '',
    isArticleView && isAIPanelActuallyVisible ? 'with-ai-panel' : '',
  ].filter(Boolean).join(' ');

  return (
    <GuidedOnboardingProvider userId={auth.user?.id}>
    <HeaderSlotProvider registry={headerSlots}>
      <div className={containerClasses}>
        {/* Trial banner — shows when ≤5 days left */}
        {auth.isAuthenticated && <TrialBanner />}
        {showsMobileTopBar && <MobileTopBar />}
        {/* The page supplies its own title and actions, so the bar is only the
            surface they land on — same geometry as the list bar above. */}
        {showsMobilePageBar && (
          <header
            className="mobile-top-bar mobile-top-bar--page"
            ref={headerSlots.slotRef('page-header-slot')}
          />
        )}
        {/* The sidebar owns the leftmost column top to bottom, so it leads the
            header in reading and tab order; both are fixed, and their stacking
            is set by z-index rather than by source order. */}
        {!isMobileShell && <Sidebar />}
        {!isMobileShell && <header className="header">
          <div className="header-content">
            {isArticleView && (
              <div className="header-left">
                <div
                  id="reader-header-navigation-slot"
                  className="reader-header-navigation-slot"
                  ref={headerSlots.slotRef('reader-header-navigation-slot')}
                />
              </div>
            )}
            {isDeckView && (
              <div
                id="deck-header-slot"
                className="deck-header-slot"
                ref={headerSlots.slotRef('deck-header-slot')}
              />
            )}
            {isListView && (
              <div
                id="list-header-slot"
                className="list-header-slot"
                ref={headerSlots.slotRef('list-header-slot')}
              />
            )}
            {isContentPage && (
              <div
                className="page-header-slot"
                ref={headerSlots.slotRef('page-header-slot')}
              />
            )}
            {isArticleView && (
              <div
                id="reader-header-actions-slot"
                className="reader-header-actions-slot"
                ref={headerSlots.slotRef('reader-header-actions-slot')}
              />
            )}
            <div className="nav-links">
              {auth.isAuthenticated && (
                <>
                  {isArticleView && <CommentControlBar />}
                  {!isReviewRoute && (
                    <IconButton
                      label={t('sidebar:footer.feedback')}
                      variant="ghost"
                      size="lg"
                      className="topbar-feedback-btn"
                      onClick={() => openFeedbackWidget()}
                      title={t('sidebar:footer.feedback')}
                    >
                      <MessageCircleQuestionMark size={ICON_SIZE.sm} />
                    </IconButton>
                  )}
                </>
              )}
            </div>
          </div>
        </header>}
        {renderContent()}
        {showsMobileTabBar && <MobileTabBar />}
        {showExtensionModal && <ExtensionModal />}
        {showConfirmDeleteModal && <ConfirmDeleteModal />}
        {showEditTitleModal && <EditTitleModal />}
        {showTagEditModal && <TagEditModal />}
        {/* Global upload modal — shared by Sidebar Add and compact header + */}
        {auth.isAuthenticated && <GlobalUploadModal />}
        {/* Trial end summary — full-screen overlay shown once after trial expires */}
        {auth.isAuthenticated && <TrialEndSummary />}
      </div>
    </HeaderSlotProvider>
    </GuidedOnboardingProvider>
  );
};

export default Layout;
